import { RouteTypeEnum, TripTypeEnum } from '@/types/enums'

type TypeValue = null | number | string | undefined

const toNumber = (value: TypeValue): null | number => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/** Route types a tenant route (and its contract) can have. There is no route type 3. */
export const ROUTE_TYPES = [
  RouteTypeEnum.FIXED_ROUTE,
  RouteTypeEnum.FIXED_DESTINATION,
  RouteTypeEnum.RESTRICTED_FIXED_DESTINATION
] as const

/** `true` for a route type, or a contract type that routes can belong to (1, 2, 4). */
export const isRouteType = (value: TypeValue): boolean =>
  (ROUTE_TYPES as readonly number[]).includes(toNumber(value) ?? NaN)

/**
 * Trip type generated from a route type. Explicit on purpose: route type 4
 * (Restricted Fixed Destination) maps to trip type 5, never to 4 (Special Request).
 */
export function routeTypeToTripType(routeType: TypeValue): null | TripTypeEnum {
  switch (toNumber(routeType)) {
    case RouteTypeEnum.FIXED_DESTINATION:
      return TripTypeEnum.FIXED_DESTINATION
    case RouteTypeEnum.FIXED_ROUTE:
      return TripTypeEnum.FIXED_ROUTE
    case RouteTypeEnum.RESTRICTED_FIXED_DESTINATION:
      return TripTypeEnum.RESTRICTED_FIXED_DESTINATION
    default:
      return null
  }
}

/** Route type a route-based trip was generated from (inverse of `routeTypeToTripType`). */
export function tripTypeToRouteType(tripType: TypeValue): null | RouteTypeEnum {
  switch (toNumber(tripType)) {
    case TripTypeEnum.FIXED_DESTINATION:
      return RouteTypeEnum.FIXED_DESTINATION
    case TripTypeEnum.FIXED_ROUTE:
      return RouteTypeEnum.FIXED_ROUTE
    case TripTypeEnum.RESTRICTED_FIXED_DESTINATION:
      return RouteTypeEnum.RESTRICTED_FIXED_DESTINATION
    default:
      return null
  }
}

/** Route types 2 and 4 share fixed-destination behaviour (stop locks, reservations). */
export const isFixedDestinationFamilyRouteType = (value: TypeValue): boolean => {
  const type = toNumber(value)
  return (
    type === RouteTypeEnum.FIXED_DESTINATION ||
    type === RouteTypeEnum.RESTRICTED_FIXED_DESTINATION
  )
}

/** Trip types 2 and 5 share fixed-destination behaviour (finalize, split, approval, stop locks). */
export const isFixedDestinationFamilyTripType = (value: TypeValue): boolean => {
  const type = toNumber(value)
  return (
    type === TripTypeEnum.FIXED_DESTINATION ||
    type === TripTypeEnum.RESTRICTED_FIXED_DESTINATION
  )
}

/** `true` for trip types generated from a tenant route (1, 2, 5). */
export const isRouteBasedTripType = (value: TypeValue): boolean =>
  tripTypeToRouteType(value) !== null
